import { useQuery } from '@apollo/client';
import {
  type SearchAndFiltersConfig,
  SearchAndFiltersPanel,
} from '@openx/components-core/lib/SelectionDrawer/components/SearchAndFiltersPanel';
import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext';
import { FiltersBar } from '@openx/components-filters/lib/FiltersBar/FiltersBar';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GetTaxonomyMetadataDocument } from '../../../../../../../../graphql/query/taxonomy/GetTaxonomyMetadata.generated';
import { AudienceFields, type AudienceForm } from '../../../../../../../../modules/audiences/AudienceForm/types';
import { TAXONOMY_METADATA_CATEGORIES_WITH_DIRECT } from '../../../../../../../../modules/taxonomy/constants';
import { mapTaxonomyFilters } from '../../../../../../../../utils/mapTaxonomyFilters';
import { exportType } from '../../../../../constants';
import type { OrganizationMap, ProviderMap } from '../../../hooks/useReferenceData';
import { type ForbiddenCategory, forbiddenCategories } from '../../../hooks/utils/defaultFilters';
import { createFiltersProviderConfig } from '../utils/segmentUtils';
import type { SegmentFilters } from './SegmentsList/types';
import { DATA_SOURCE_OPTIONS, type DataSourceOptions, dataSourceOptions } from './types';

export type SearchAndFiltersProps = {
  searchPhrase: string;
  setSearchPhrase: (value: string) => void;
  filters: SegmentFilters;
  onFiltersChange: (filters: SegmentFilters) => void;
  providers: ProviderMap;
  organizations: OrganizationMap;
  hasLinkedProviders: boolean;
  isLookalike: boolean;
  dataSourceFilter: DataSourceOptions;
  setDataSourceFilter: (value: DataSourceOptions) => void;
  hideDataTypeFilter?: boolean;
  hideDataProviderFilter?: boolean;
  hideReachFilter?: boolean;
  hasOxpsSegments?: boolean;
};

export const SearchAndFilters = ({
  searchPhrase,
  setSearchPhrase,
  filters,
  onFiltersChange,
  providers,
  organizations,
  hasLinkedProviders,
  isLookalike,
  dataSourceFilter,
  setDataSourceFilter,
  hideDataTypeFilter = false,
  hideDataProviderFilter = false,
  hideReachFilter = false,
  hasOxpsSegments = false,
}: SearchAndFiltersProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<AudienceForm>();

  const observableDirectProvider = useWatch({ control, name: AudienceFields.DirectAudienceProvider });
  const observableMatchType = useWatch({ control, name: AudienceFields.ExportType });

  const { data: { segment_aggregate: { nodes: metadata = [] } = {} } = {} } = useQuery(GetTaxonomyMetadataDocument, {
    variables: {
      categories: [...TAXONOMY_METADATA_CATEGORIES_WITH_DIRECT],
    },
  });

  const { categories, subCategories } = useMemo(() => mapTaxonomyFilters(metadata), [metadata]);
  const filteredCategories = useMemo(
    () => categories.filter(category => !forbiddenCategories.has(category as ForbiddenCategory)),
    [categories]
  );

  const showDataFilter =
    (!observableDirectProvider || observableMatchType === exportType.OA_MATCH) && hasLinkedProviders && !isLookalike;

  const hideDataTypeFilterByDataSource =
    dataSourceFilter === dataSourceOptions.MyData || dataSourceFilter === dataSourceOptions.oxs3pData;

  const shouldHideDataTypeFilter = hideDataTypeFilter || hideDataTypeFilterByDataSource;

  const filtersProviderConfig = useMemo(
    () =>
      createFiltersProviderConfig(
        t,
        filters,
        onFiltersChange,
        filteredCategories,
        subCategories,
        providers,
        organizations,
        observableDirectProvider,
        observableMatchType,
        isLookalike,
        shouldHideDataTypeFilter,
        hideDataProviderFilter,
        hideReachFilter,
        hasOxpsSegments
      ),
    [
      t,
      filters,
      onFiltersChange,
      filteredCategories,
      subCategories,
      providers,
      observableDirectProvider,
      observableMatchType,
      isLookalike,
      shouldHideDataTypeFilter,
      hideDataProviderFilter,
      hideReachFilter,
      organizations,
      hasOxpsSegments,
    ]
  );

  const sourceToLabel = useCallback(
    (source: DataSourceOptions) => {
      switch (source) {
        case dataSourceOptions.All:
          return t('All Segments');
        case dataSourceOptions.MyData:
          return t('My Data');
        case dataSourceOptions.OpenXSelectData:
          return t('OXS Proprietary Segments');
        case dataSourceOptions.oxs3pData:
          return t('3rd Party Segments');
        default:
          return source;
      }
    },
    [t]
  );

  const searchAndFiltersConfig: SearchAndFiltersConfig = useMemo(
    () => ({
      search: {
        value: searchPhrase,
        onChange: setSearchPhrase,
        placeholder: t('Search for Segments'),
        dataTest: 'searchbar',
      },
      prefixFilter: showDataFilter
        ? {
            options: DATA_SOURCE_OPTIONS.map(option => ({
              value: option,
              label: sourceToLabel(option),
            })),
            value: dataSourceFilter,
            onChange: setDataSourceFilter as (value: string) => void,
            ariaLabel: t('Data source'),
            dataTest: 'data-source-filter',
          }
        : undefined,
      filters: (
        <FiltersContextProvider config={filtersProviderConfig}>
          <FiltersBar />
        </FiltersContextProvider>
      ),
    }),
    [
      searchPhrase,
      setSearchPhrase,
      t,
      showDataFilter,
      dataSourceFilter,
      setDataSourceFilter,
      filtersProviderConfig,
      sourceToLabel,
    ]
  );

  return <SearchAndFiltersPanel config={searchAndFiltersConfig} />;
};
